import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../services/auth';
import { migrationApi } from '../services/api';

const MigrationUpdated = () => {
  const [migrationRequests, setMigrationRequests] = useState([]);
  const [loadingMigrations, setLoadingMigrations] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Check if user is authenticated
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
    }
  }, [navigate]);

  // Fetch migration requests on component mount
  useEffect(() => {
    fetchMigrationRequests();
  }, []);

  const fetchMigrationRequests = async () => {
    setLoadingMigrations(true);
    setError(null);
    try {
      const response = await migrationApi.getAllMigrationRequests(1, 50, true);

      if (response.data) {
        if (response.data.data && Array.isArray(response.data.data)) {
          setMigrationRequests(response.data.data);
        } else if (Array.isArray(response.data)) {
          setMigrationRequests(response.data);
        } else if (
          response.data.response &&
          response.data.response.data &&
          Array.isArray(response.data.response.data)
        ) {
          setMigrationRequests(response.data.response.data);
        } else {
          setMigrationRequests([]);
        }
      } else {
        setMigrationRequests([]);
      }
    } catch (err) {
      console.error('Error fetching migration requests:', err);
      setError('Failed to load migration requests. Please try again.');
      setMigrationRequests([]);
    } finally {
      setLoadingMigrations(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-title-md font-semibold text-gray-900 dark:text-white">
            Migration
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Migrate customer accounts from external systems. Manage migration requests and monitor progress.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(`/migrations/create`)}
            className="autobot-button-primary"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Migration
          </button>
          <button 
            onClick={fetchMigrationRequests}
            disabled={loadingMigrations}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className={`w-4 h-4 ${loadingMigrations ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loadingMigrations ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-error-50 border border-error-200 text-error-700 px-4 py-3 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <strong className="font-bold">Error loading migration requests!</strong>
              <span className="block sm:inline mt-1">{error}</span>
            </div>
            <button
              onClick={fetchMigrationRequests}
              className="px-3 py-1 bg-error-600 text-white rounded-md hover:bg-error-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loadingMigrations && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-500 border-t-transparent"></div>
        </div>
      )}

      {/* Migration Requests Table */}
      {!loadingMigrations && migrationRequests.length > 0 && (
        <div className="relative bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 shadow-theme-sm">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Migration Requests ({migrationRequests.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Source Game
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Destination Game
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                {migrationRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      #{request.id || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex flex-col">
                        <div className="font-medium">{request.categorySource}</div>
                        <div className="text-xs text-gray-400 truncate max-w-xs">
                          {request.linkSource}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex flex-col">
                        <div className="font-medium">{request.categoryDes}</div>
                        <div className="text-xs text-gray-400 truncate max-w-xs">
                          {request.linkDes}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-400">
                          {request.itemStats ? 
                            `${request.itemStats.successful || 0}/${request.itemStats.total || 0}` : 
                            '0/0'
                          }
                        </span>
                        {request.itemStats && request.itemStats.total > 0 && (
                          <div className="w-16 bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                            <div 
                              className="bg-brand-500 h-2 rounded-full transition-all duration-300"
                              style={{ 
                                width: `${Math.round((request.itemStats.successful || 0) / request.itemStats.total * 100)}%` 
                              }}
                            ></div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {request.createdAt ? new Date(request.createdAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => navigate(`/migrations/${request.id}`)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View Items
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data */}
      {!loadingMigrations && migrationRequests.length === 0 && !error && (
        <div className="text-center py-12 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" view-box="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No migration requests</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Get started by creating a new migration request.
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate(`/migrations/create`)}
              className="autobot-button-primary"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Migration
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MigrationUpdated;
